import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Fab,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
} from '@mui/material';
import {
  Description as TemplatesIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { Template } from '../../utils/templateApi';
import templateApi from '../../utils/templateApi';
import TemplateFormDialog from '../../components/admin/TemplateFormDialog';
import TemplateViewDialog from '../../components/admin/TemplateViewDialog';
import toast from 'react-hot-toast';

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [templatesPerPage] = useState(12);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  const categories = Array.from(new Set(templates.map(t => t.category).filter(Boolean)));
  const types = ['Lead', 'Assistente', 'Ambos'];

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templatesData = await templateApi.fetchTemplates();
      setTemplates(templatesData);
      setFilteredTemplates(templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    let filtered = templates;

    if (searchText.trim() !== '') {
      const query = searchText.toLowerCase();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(query) ||
        template.description.toLowerCase().includes(query) ||
        template.category.toLowerCase().includes(query)
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(template => template.category === categoryFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter(template => template.type === typeFilter);
    }

    setFilteredTemplates(filtered);
    setPage(1);
  }, [searchText, categoryFilter, typeFilter, templates]);

  const handleBackClick = () => {
    navigate('/admin/super-admin');
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setFormDialogOpen(true);
  };

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setFormDialogOpen(true);
  };

  const handleViewTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setTimeout(() => {
      setViewDialogOpen(true);
    }, 0);
  };

  const handleTemplateSaved = (savedTemplate: Template) => {
    if (editingTemplate) {
      // Update existing template
      setTemplates(prev =>
        prev.map(template =>
          template.id === savedTemplate.id ? savedTemplate : template
        )
      );
    } else {
      // Add new template
      setTemplates(prev => [...prev, savedTemplate]);
    }
    setFormDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      const duplicatedTemplate = {
        ...template,
        name: `${template.name} (Cópia)`,
        isDefault: false,
      };
      delete (duplicatedTemplate as any).id;
      delete (duplicatedTemplate as any).active;

      await templateApi.createTemplate(duplicatedTemplate);
      toast.success('Template duplicado com sucesso!');
      loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Erro ao duplicar template');
    }
  };

  const handleDeleteTemplate = (template: Template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await templateApi.deleteTemplate(templateToDelete.id);
      toast.success('Template deletado com sucesso!');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Erro ao deletar template');
    } finally {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: Template) => {
    setMenuAnchor(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTemplate(null);
  };

  const getStatusColor = (status: string) => {
    return status === 'Enabled' ? 'success' : 'error';
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const paginatedTemplates = filteredTemplates.slice(
    (page - 1) * templatesPerPage,
    page * templatesPerPage
  );

  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: { xs: 2, md: 3 },
        flexWrap: 'wrap',
        gap: { xs: 1, md: 0 }
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mr: 2, textTransform: 'none' }}
        >
          {window.innerWidth < 768 ? '' : 'Voltar'}
        </Button>
        <TemplatesIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Templates
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar templates..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250, flexGrow: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Categoria"
            >
              <SelectMenuItem value="">Todas</SelectMenuItem>
              {categories.map((category) => (
                <SelectMenuItem key={category} value={category}>
                  {category}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Tipo"
            >
              <SelectMenuItem value="">Todos</SelectMenuItem>
              {types.map((type) => (
                <SelectMenuItem key={type} value={type}>
                  {type}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template encontrado' : 'templates encontrados'}
          </Typography>

          {!isMobile && (
            <Button
              variant="contained"
              sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
              size="large"
            >
              Novo Template
            </Button>
          )}
        </Box>
      </Paper>

      {filteredTemplates.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Nenhum template encontrado
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {searchText || categoryFilter || typeFilter
              ? 'Tente ajustar os filtros de busca'
              : 'Crie seu primeiro template para começar'
            }
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: 3
          }}>
            {paginatedTemplates.map((template) => (
              <Card key={template.id} elevation={3} sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'box-shadow 120ms ease, transform 120ms ease',
                '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' }
              }}>
                <CardContent sx={{ 
                  flexGrow: 1, 
                  p: { xs: 2, md: 3 },
                  '&:last-child': { pb: { xs: 2, md: 3 } }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: { xs: 1.5, md: 2 },
                    gap: 1
                  }}>
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      sx={{ 
                        fontWeight: 700, 
                        flexGrow: 1,
                        fontSize: { xs: '1rem', md: '1.25rem' },
                        lineHeight: { xs: 1.3, md: 1.4 }
                      }}
                    >
                      {template.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, template)}
                      sx={{ flexShrink: 0 }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: { xs: 1.5, md: 2 }, 
                      minHeight: { xs: 32, md: 40 },
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      lineHeight: { xs: 1.4, md: 1.5 }
                    }}
                  >
                    {truncateText(template.description, window.innerWidth < 768 ? 80 : 100)}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, md: 1 }, 
                    flexWrap: 'wrap', 
                    mb: { xs: 1.5, md: 2 },
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <Chip
                      label={template.type}
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? '#000' : undefined,
                        fontSize: { xs: '0.7rem', md: '0.75rem' }
                      }}
                      size="small"
                    />
                    <Chip
                      label={template.status}
                      size="small"
                      color={getStatusColor(template.status) as any}
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                    />
                    {template.category && (
                      <Chip
                        label={template.category}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                      />
                    )}
                  </Box>

                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.7rem', md: '0.75rem' },
                      textAlign: { xs: 'center', sm: 'left' },
                      display: 'block'
                    }}
                  >
                    Modelo: {template.model} • Delay: {template.responseDelay}ms
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: { xs: 3, md: 4 },
              px: { xs: 2, md: 0 }
            }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleCreateTemplate}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            transition: 'transform 120ms ease, box-shadow 120ms ease',
            '&:hover': { 
              boxShadow: 8, 
              transform: 'translateY(-2px)' 
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Template Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { 
          handleMenuClose(); 
          setTimeout(() => handleViewTemplate(selectedTemplate!), 0); 
        }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Visualizar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { 
          handleMenuClose(); 
          setTimeout(() => handleEditTemplate(selectedTemplate!), 0); 
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { 
          handleMenuClose(); 
          setTimeout(() => handleDuplicateTemplate(selectedTemplate!), 0); 
        }}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { 
          handleMenuClose(); 
          setTimeout(() => handleDeleteTemplate(selectedTemplate!), 0); 
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Deletar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar o template "{templateToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmDeleteTemplate} color="error" variant="contained">
            Deletar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Form Dialog */}
      <TemplateFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingTemplate(null);
        }}
        onSaved={handleTemplateSaved}
        template={editingTemplate}
      />

      {/* Template View Dialog */}
      <TemplateViewDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onEdit={handleEditTemplate}
        onDuplicate={handleDuplicateTemplate}
        onDelete={handleDeleteTemplate}
      />
    </Container>
  );
};

export default TemplatesPage;
